/* ============================================
   PDF VIEWER COMPONENT
   The Creative Orchestra - GEMINI Framework
   Embedded PDF viewer for research papers
   ============================================ */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Download, 
  ExternalLink, 
  Maximize2,
  Minimize2,
  X,
  AlertCircle,
} from 'lucide-react';
import { useTheme } from '@context/ThemeContext';

/**
 * PDF Viewer Component
 * Embedded PDF viewer with fullscreen option
 * 
 * @param {Object} props
 * @param {string} props.url - PDF file URL
 * @param {string} props.title - Document title
 * @param {string} props.workId - Work ID for path construction
 */
const PDFViewer = ({ 
  url, 
  title = 'Research Paper',
  workId,
  className = '',
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { activeTheme } = useTheme();

  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  // Construct full URL if needed
  const pdfUrl = url?.startsWith('http') || url?.startsWith('/') 
    ? url 
    : `/assets/papers/${workId}-paper.pdf`;

  if (!url && !workId) {
    return (
      <div className={`p-8 rounded-xl bg-white/5 border border-white/10 text-center ${className}`}>
        <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500">Paper coming soon...</p>
      </div>
    );
  }

  return (
    <>
      <section className={`py-12 px-4 ${className}`}>
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `rgba(${accentRgb}, 0.2)` }}
              >
                <FileText className="w-5 h-5" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="text-sm text-gray-500">Research Paper</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <motion.a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg
                  text-sm text-gray-400 hover:text-white
                  bg-white/5 hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ExternalLink className="w-4 h-4" />
                Open
              </motion.a>

              <motion.a
                href={pdfUrl}
                download
                className="flex items-center gap-2 px-3 py-2 rounded-lg
                  text-sm text-gray-400 hover:text-white
                  bg-white/5 hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Download className="w-4 h-4" />
                Download
              </motion.a>

              <motion.button
                onClick={() => setIsFullscreen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg
                  text-sm font-medium transition-colors"
                style={{
                  background: `rgba(${accentRgb}, 0.1)`,
                  color: accentColor,
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Maximize2 className="w-4 h-4" />
                Fullscreen
              </motion.button>
            </div>
          </div>

          {/* Embedded PDF */}
          <div 
            className="relative rounded-xl overflow-hidden border"
            style={{ 
              borderColor: `rgba(${accentRgb}, 0.2)`,
              height: '600px',
            }}
          >
            {hasError ? (
              <PDFErrorState pdfUrl={pdfUrl} />
            ) : (
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0`}
                title={title}
                className="w-full h-full bg-white"
                onError={() => setHasError(true)}
              />
            )}
          </div>

          {/* Hint */}
          <p className="text-center text-xs text-gray-600 mt-4">
            Click "Fullscreen" for better reading experience
          </p>
        </div>
      </section>

      {/* Fullscreen Modal */}
      <PDFFullscreen
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        url={pdfUrl}
        title={title}
      />
    </>
  );
};

/**
 * PDF Fullscreen Modal
 */
const PDFFullscreen = ({ isOpen, onClose, url, title }) => {
  // Prevent body scroll when open
  useState(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] bg-black"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 p-4
            flex items-center justify-between
            bg-gradient-to-b from-black to-transparent">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-white/80" />
              <span className="text-white font-medium">{title}</span>
            </div>

            <div className="flex items-center gap-2">
              <motion.a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg text-white/80 hover:text-white
                  hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.05 }}
                title="Open in new tab"
              >
                <ExternalLink className="w-5 h-5" />
              </motion.a>

              <motion.a
                href={url}
                download
                className="p-2 rounded-lg text-white/80 hover:text-white
                  hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.05 }}
                title="Download"
              >
                <Download className="w-5 h-5" />
              </motion.a>

              <motion.button
                onClick={onClose}
                className="p-2 rounded-lg text-white/80 hover:text-white
                  hover:bg-red-500/20 transition-colors"
                whileHover={{ scale: 1.05 }}
                title="Close (Esc)"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* PDF */}
          <iframe
            src={url}
            title={title}
            className="w-full h-full bg-white pt-16"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * PDF Error State
 */
const PDFErrorState = ({ pdfUrl }) => {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center
      bg-white/5 text-center p-8">
      <AlertCircle className="w-12 h-12 text-gray-500 mb-4" />
      <h4 className="text-white font-medium mb-2">
        Unable to display PDF
      </h4>
      <p className="text-gray-400 text-sm mb-6 max-w-md">
        Your browser may not support embedded PDF viewing. 
        Please download the file or open it in a new tab.
      </p>
      <div className="flex items-center gap-3">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-lg
            bg-indigo-500 text-white text-sm font-medium
            hover:bg-indigo-400 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Open in New Tab
        </a>
        <a
          href={pdfUrl}
          download
          className="flex items-center gap-2 px-4 py-2 rounded-lg
            bg-white/10 text-white text-sm font-medium
            hover:bg-white/20 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download
        </a>
      </div>
    </div>
  );
};

/**
 * PDF Card Component (compact link card)
 */
export const PDFCard = ({ 
  url, 
  title = 'Research Paper',
  description,
  workId,
  className = '',
}) => {
  const { activeTheme } = useTheme();
  const accentColor = activeTheme?.colors?.accent || '#818CF8';
  const accentRgb = activeTheme?.cssVariables?.['--theme-accent-rgb'] || '129, 140, 248';

  const pdfUrl = url?.startsWith('http') || url?.startsWith('/') 
    ? url 
    : `/assets/papers/${workId}-paper.pdf`;

  return (
    <motion.a
      href={pdfUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`block p-6 rounded-xl border transition-all duration-300 group ${className}`}
      style={{
        background: `rgba(${accentRgb}, 0.05)`,
        borderColor: `rgba(${accentRgb}, 0.1)`,
      }}
      whileHover={{
        y: -4,
        borderColor: `rgba(${accentRgb}, 0.3)`,
      }}
    >
      <div className="flex items-start gap-4">
        <div 
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: `rgba(${accentRgb}, 0.2)` }}
        >
          <FileText className="w-6 h-6" style={{ color: accentColor }} />
        </div>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-white group-hover:text-indigo-300 transition-colors">
              {title}
            </h4>
            <ExternalLink className="w-4 h-4 text-gray-500" />
          </div>
          {description && (
            <p className="text-sm text-gray-400">{description}</p>
          )}
          <div className="flex items-center gap-4 mt-3">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              View PDF
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Download className="w-3 h-3" />
              Download
            </span>
          </div>
        </div>
      </div>
    </motion.a>
  );
};

/**
 * Publications List Component
 */
export const PublicationsList = ({ publications, className = '' }) => {
  if (!publications || publications.length === 0) return null;

  return (
    <section className={`py-12 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto">
        <h3 className="text-xl font-semibold text-white mb-6">Publications</h3>
        <div className="space-y-4">
          {publications.map((pub, index) => (
            <motion.div
              key={index}
              className="p-4 rounded-xl bg-white/5 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <h4 className="text-white font-medium mb-2">{pub.title}</h4>
              {pub.authors && (
                <p className="text-sm text-gray-400 mb-1">{pub.authors}</p>
              )}
              {pub.venue && (
                <p className="text-sm text-gray-500 italic">{pub.venue}</p>
              )}
              {pub.year && (
                <p className="text-xs text-gray-600 mt-2">{pub.year}</p>
              )}
              {pub.url && (
                <a
                  href={pub.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm
                    text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  View Paper
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PDFViewer;
